import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  HouseLineIcon,
  MagnifyingGlassIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import { globalSearch, type SearchHit } from '@/features/search/api';
import { Input, cn } from '@smena/ui';

const ICON: Record<SearchHit['type'], React.ReactNode> = {
  guest: <UsersThreeIcon weight="bold" className="size-4" />,
  booking: <CalendarIcon weight="bold" className="size-4" />,
  property: <HouseLineIcon weight="bold" className="size-4" />,
};

export function CmdK() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const hitsQ = useQuery({
    queryKey: ['search', q],
    queryFn: () => globalSearch(q),
    enabled: open && q.trim().length >= 2,
    staleTime: 5_000,
  });

  useEffect(() => {
    setCursor(0);
  }, [hitsQ.data]);

  if (!open) return null;

  const items = hitsQ.data ?? [];

  function go(hit: SearchHit) {
    navigate(hit.href);
    setOpen(false);
    setQ('');
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(items.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      const hit = items[cursor];
      if (hit) go(hit);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-ink/45 p-4 pt-24"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-line bg-panel shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-line px-3 py-2">
          <MagnifyingGlassIcon weight="bold" className="size-4 text-muted" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Поиск: имя гостя, объект, id брони…"
            className="border-none bg-transparent focus-visible:outline-none"
          />
          <kbd className="rounded bg-panel2 px-1.5 py-0.5 text-[10px] text-muted">ESC</kbd>
        </div>

        <div className="max-h-80 overflow-y-auto py-1">
          {q.trim().length < 2 ? (
            <div className="p-6 text-center text-xs text-muted">Введи хотя бы 2 символа</div>
          ) : hitsQ.isLoading ? (
            <div className="p-6 text-center text-xs text-muted">Ищем…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted">Ничего не нашлось</div>
          ) : (
            items.map((h, i) => (
              <button
                key={`${h.type}:${h.id}`}
                type="button"
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(h)}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors',
                  i === cursor ? 'bg-accent/20' : 'hover:bg-panel2',
                )}
              >
                <span
                  className={cn(
                    'grid size-7 place-items-center rounded-md',
                    h.type === 'guest' && 'bg-accent/20 text-accent-fg',
                    h.type === 'booking' && 'bg-green/20 text-green',
                    h.type === 'property' && 'bg-amber/20 text-amber',
                  )}
                >
                  {ICON[h.type]}
                </span>
                <span className="flex-1">
                  <span className="block text-sm">{h.title}</span>
                  {h.subtitle ? <span className="block text-[11px] text-muted">{h.subtitle}</span> : null}
                </span>
                <kbd className="rounded bg-panel2 px-1.5 py-0.5 text-[10px] text-muted">↵</kbd>
              </button>
            ))
          )}
        </div>
        <div className="border-t border-line px-3 py-1.5 text-[10px] text-muted">
          <kbd className="rounded bg-panel2 px-1 py-0.5">⌘K</kbd> открыть · <kbd className="rounded bg-panel2 px-1 py-0.5">↑↓</kbd> навигация · <kbd className="rounded bg-panel2 px-1 py-0.5">↵</kbd> открыть
        </div>
      </div>
    </div>
  );
}
